import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../utils/api';
import '../styles/CreateNewAdmin.css';
import CloseIcon from '@mui/icons-material/Close';
import logger from '../utils/logger';
import { useTenant } from '../context/TenantContext';

const CreateNewAdmin = ({ onClose, onSuccess }) => {
  const [creating, setCreating] = useState(false);
  const { tenant } = useTenant();
  const clinicName = tenant?.brandName || tenant?.name || 'this clinic';

  // Formik Setup
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      accountType: 'clinic',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email').required('Email is required'),
      password: Yup.string().min(6, 'Minimum 6 characters').required('Password is required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        setCreating(true);
        const { accountType, ...account } = values;
        // A clinic admin belongs to the selected clinic; a super admin manages every clinic.
        await api.post(accountType === 'super' ? '/api/super-admins' : '/api/register', account);

        alert('Admin Created Successfully');
        resetForm();
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (error) {
        logger.error(error);
        if (error.response && error.response.data) {
          const msg = error.response.data.message || '';
          if (msg.toLowerCase().includes('already exists') || msg.toLowerCase().includes('exists')) {
            alert('Admin already exists!');
          } else {
            alert(msg || 'Admin creation failed!');
          }
        } else {
          alert('Something went wrong!');
        }
      } finally {
        setCreating(false);
      }
    },
  });

  const fields = [
    { name: 'email', label: 'Email address', type: 'email' },
    { name: 'password', label: 'Password', type: 'password' },
  ];

  return (
    <div className='modal-overlay'>
      <div className='create-admin-popup'>
        {/* Close Button */}
        <button
          onClick={onClose}
          type="button"
          className='popup-close-btn'
        >
          <CloseIcon />
        </button>

        {/* Original Form UI */}
        <form className='signup-box' onSubmit={formik.handleSubmit}>
          <p className='txt-center signup-heading'>Create New Admin</p>
          <p className='txt-center signup-txt'>
            Start creating the best possible user experience for your customers
          </p>

          <div className='floating-label-group'>
            <select
              id='accountType'
              name='accountType'
              className='signup-input has-value'
              value={formik.values.accountType}
              onChange={formik.handleChange}
            >
              <option value='clinic'>Clinic admin for {clinicName}</option>
              <option value='super'>Super admin (all clinics)</option>
            </select>
            <label htmlFor='accountType'>Account type</label>
          </div>

          {fields.map((field) => (
            <div className='floating-label-group' key={field.name}>
              <input
                type={field.type}
                id={field.name}
                name={field.name}
                className={`signup-input ${
                  formik.values[field.name] ? 'has-value' : ''
                } ${formik.touched[field.name] && formik.errors[field.name] ? 'has-error' : ''}`}
                value={formik.values[field.name]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required
                autoComplete='off'
              />
              <label
                htmlFor={field.name}
                className={formik.touched[field.name] && formik.errors[field.name] ? 'has-error' : ''}
              >
                {field.label}
              </label>

              {formik.touched[field.name] && formik.errors[field.name] && (
                <div className='error-msg'>{formik.errors[field.name]}</div>
              )}
            </div>
          ))}

          <button className='signup-btn' type='submit' disabled={creating}>
            {creating ? 'Creating...' : 'Add New Admin'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateNewAdmin;